import * as React from 'react';
import { useObservable } from 'react-use-observable';

import { Alert, Card, Typography } from 'antd';

import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { Field, useFormikContext } from 'formik';
import { formatDate } from 'helpers/date';
import ExportWarning from 'v2/components/Shared/ExportWarning';
import { CheckboxFormField } from 'v2/components/Shared/FormFields/CheckboxFormField';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import useGetAllowedEmailsExport from 'v2/hooks/useGetAllowedEmailsExport';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import type { ExportStatement } from '../constants';

const today = startOfDay(new Date());
const datePreset = [
  {
    id: 'week',
    text: '7 dias',
    start: subDays(today, 7),
    end: today
  },
  {
    id: 'halfMonth',
    text: '15 dias',
    start: subDays(today, 15),
    end: today
  },
  {
    id: 'month',
    text: '30 dias',
    start: subDays(today, 30),
    end: today
  },
  {
    id: 'twoMonth',
    text: '60 dias',
    start: subDays(today, 60),
    end: today
  },
  {
    id: 'threeMonth',
    text: '90 dias',
    start: subDays(today, 90),
    end: today
  },
  {
    id: 'fourMonth',
    text: '120 dias',
    start: subDays(today, 120),
    end: today
  },
  {
    id: 'sixMonth',
    text: '180 dias',
    start: subDays(today, 180),
    end: today
  }
];

function Form() {
  const { values, setFieldValue, errors } = useFormikContext<ExportStatement>();
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [filters] = useObservable(() => financialStatementService.getFilters(), []);
  const { isLoading, data: emailsAllowedExport } = useGetAllowedEmailsExport();
  const emailsAllowedExportFormatted = emailsAllowedExport.sort().map(email => ({
    label: email,
    value: email
  }));

  React.useEffect(() => {
    if (filters?.periodPreset) {
      setFieldValue('date', [
        formatDate(subDays(new Date(), Number(filters.periodPreset)), 'dd/MM/yyyy'),
        formatDate(new Date(), 'dd/MM/yyyy')
      ]);
    }
  }, [filters?.periodPreset, setFieldValue]);

  React.useEffect(() => {
    if (user?.email && !values.email) {
      setFieldValue('email', user.email);
    }
  }, [user?.email, values.email, setFieldValue]);

  return (
    <>
      <ExportWarning />

      <div className='mb-2 flex flex-col items-start'>
        <Card size='small' variant={'borderless'} className='mb-2 bg-(--uizz-antd-color-fill-tertiary)'>
          <div className='flex w-full flex-wrap items-center gap-2'>
            <Field
              name='financial_statement'
              checked
              label='Extrato financeiro'
              component={CheckboxFormField}
              fullWidth={false}
            />
            <div className='flex items-center gap-1'>
              <Typography.Text type='secondary' className='whitespace-nowrap'>
                Período
              </Typography.Text>
              <Field
                size='small'
                id='date'
                name='date'
                customPreset={datePreset}
                component={DateRangePickerFormField}
              />
            </div>
          </div>
        </Card>
        {errors.date && <span className='text-xs text-(--uizz-antd-color-error)'>{errors.date}</span>}

        <Field name='export_in_future' label='Lançamentos futuros' component={CheckboxFormField} fullWidth={false} />

        <Field name='export_blocked' label='Lançamentos bloqueados' component={CheckboxFormField} fullWidth={false} />
      </div>

      <Field
        id='email'
        name='email'
        label='E-mail'
        placeholder='Selecione um e-mail'
        options={emailsAllowedExportFormatted}
        component={SelectFormField}
        loading={isLoading}
        fullWidth
      />

      {!!values.email && !errors.email && values?.email !== user?.email && (
        <Alert
          className='mt-2!'
          type='warning'
          showIcon
          message='Esse e-mail é diferente do e-mail de cadastro, tem certeza que deseja enviar para esse e-mail?'
        />
      )}
    </>
  );
}

export default Form;
